import { useCallback, useEffect, useState } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Button from '@cloudscape-design/components/button';
import Box from '@cloudscape-design/components/box';
import Badge from '@cloudscape-design/components/badge';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import FormField from '@cloudscape-design/components/form-field';
import Alert from '@cloudscape-design/components/alert';
import Spinner from '@cloudscape-design/components/spinner';
import { Prompt, getPrompts, updatePrompt, resetPrompt } from './api';
import PromptHistoryModal from './PromptHistoryModal';

interface Props {
  idToken: string;
}

export default function PromptsPanel({ idToken }: Props) {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPrompts(await getPrompts(idToken));
    } catch {
      setError('Failed to load prompts');
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert type="error" action={<Button onClick={() => void load()}>Retry</Button>}>
        {error}
      </Alert>
    );
  }

  return (
    <SpaceBetween size="l">
      <Alert type="info" header="Customize the assistant">
        Edit the prompts used by the scheduling assistant. Changes apply to new conversations within a
        minute. Use <strong>Reset to default</strong> to restore the original prompt. The assignment
        rules are per pillar — only that pillar's managers (and their management chain) can edit them;
        the shared prompts are restricted to prompt administrators.
      </Alert>
      {prompts.map((p) => (
        <PromptEditor key={p.key} prompt={p} idToken={idToken} onChanged={() => void load()} />
      ))}
    </SpaceBetween>
  );
}

interface EditorProps {
  prompt: Prompt;
  idToken: string;
  onChanged: () => void;
}

function PromptEditor({ prompt, idToken, onChanged }: EditorProps) {
  const [value, setValue] = useState(prompt.content);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    setValue(prompt.content);
  }, [prompt.content]);

  const dirty = value !== prompt.content;

  // canEdit is set by the backend per user. Treat an explicit false as
  // read-only; absence (legacy response) leaves editing enabled.
  const readOnly = prompt.canEdit === false;

  const missingPlaceholder = prompt.placeholders.find(
    (ph) => ph === '{user_message}' && !value.includes(ph),
  );

  const handleSave = async () => {
    setSaving(true);
    setNotice(null);
    try {
      await updatePrompt(idToken, prompt.key, value);
      setNotice('Saved');
      onChanged();
    } catch {
      setNotice('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(`Reset "${prompt.label}" to its default content?`)) return;
    setResetting(true);
    setNotice(null);
    try {
      await resetPrompt(idToken, prompt.key);
      setNotice('Reset to default');
      onChanged();
    } catch {
      setNotice('Failed to reset');
    } finally {
      setResetting(false);
    }
  };

  return (
    <Container
      header={
        <Header
          variant="h2"
          description={prompt.description}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setShowHistory(true)} iconName="undo">
                History
              </Button>
              <Button
                onClick={() => void handleReset()}
                loading={resetting}
                disabled={readOnly || (prompt.isDefault && !dirty)}
              >
                Reset to default
              </Button>
              <Button
                variant="primary"
                onClick={() => void handleSave()}
                loading={saving}
                disabled={readOnly || !dirty || Boolean(missingPlaceholder)}
              >
                Save
              </Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween direction="horizontal" size="xs">
            <span>{prompt.label}</span>
            {prompt.isDefault ? (
              <Badge color="grey">Default</Badge>
            ) : (
              <Badge color="blue">Customized</Badge>
            )}
            {readOnly && <Badge color="red">Read-only</Badge>}
          </SpaceBetween>
        </Header>
      }
    >
      <SpaceBetween size="s">
        {readOnly && (
          <Alert type="info">
            You do not have permission to edit this prompt. It is shown read-only.
          </Alert>
        )}
        {prompt.placeholders.length > 0 && (
          <Box variant="small">
            Placeholders:{' '}
            {prompt.placeholders.map((ph) => (
              <code key={ph} style={{ marginRight: 8 }}>
                {ph}
              </code>
            ))}
          </Box>
        )}
        <FormField
          errorText={missingPlaceholder ? `The ${missingPlaceholder} placeholder is required.` : undefined}
          stretch
        >
          <Textarea
            value={value}
            onChange={({ detail }) => setValue(detail.value)}
            rows={16}
            spellcheck={false}
            readOnly={readOnly}
          />
        </FormField>
        <Box float="right" variant="small" color="text-status-inactive">
          {notice ? (
            <span>{notice}</span>
          ) : prompt.updatedBy ? (
            <span>
              Last edited by {prompt.updatedBy}
              {prompt.updatedAt ? ` on ${prompt.updatedAt}` : ''}
            </span>
          ) : null}
        </Box>
      </SpaceBetween>
      {showHistory && (
        <PromptHistoryModal
          prompt={prompt}
          idToken={idToken}
          onDismiss={() => setShowHistory(false)}
        />
      )}
    </Container>
  );
}
